import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { Field as FormikField } from 'formik';
import { DateTime } from 'luxon';
import { useQuery } from 'react-query';
import PropTypes from 'prop-types';

import { isInServiceArea } from 'api';

import { Input } from 'components/ui-kit/form';
import FormikDatepicker from 'components/ui-kit/form/formik-datepicker';
import FormikSelect from 'components/ui-kit/form/formik-select';
import LocationInput from 'components/ui-kit/form/location-input';
import { useApp } from 'hooks';

import {
  getMinDateTime,
  filterDate,
  handleDateChange,
  getKeyForDate,
  fetchMonthSlots,
} from './utils';

import * as S from '../styles';

const ContactDetailsStep = ({
  values,
  form,
  setFieldValue,
}) => {
  const { user } = useApp();
  const [currentKey, setCurrentKey] = useState('');
  const loadedMonthsRef = useRef(new Map());
  const timeSlotsMapRef = useRef(new Map());
  const minDateTime = getMinDateTime(form);
  const { showDateTimePicker, requireDateTime } = form;

  const fetchInitialEvents = useCallback((now) => {
    const nextMonth = now.plus({ months: 1 });

    fetchMonthSlots(
      now.month - 1,
      now.year,
      values,
      form,
      loadedMonthsRef.current,
      timeSlotsMapRef.current,
    );
    fetchMonthSlots(
      nextMonth.month - 1,
      nextMonth.year,
      values,
      form,
      loadedMonthsRef.current,
      timeSlotsMapRef.current,
    );
    setCurrentKey(getKeyForDate(now.toJSDate()));
  }, [form, values]);

  useEffect(() => {
    const now = DateTime.now();
    fetchInitialEvents(now);
  }, [fetchInitialEvents]);

  const handleMonthChange = (date) => {
    const newMonth = date.getMonth();
    const newYear = date.getFullYear();
    const key = getKeyForDate(date);

    if (!loadedMonthsRef.current.has(key)) {
      fetchMonthSlots(
        newMonth,
        newYear,
        values,
        form,
        loadedMonthsRef.current,
        timeSlotsMapRef.current,
      );
    }

    setCurrentKey(key);
  };

  const updateEventMap = (newDate) => {
    const selectedYear = newDate.getFullYear();
    const selectedMonth = newDate.getMonth();
    const key = getKeyForDate(newDate);

    const nextMonth = (selectedMonth + 1) % 12;
    const nextYear = selectedMonth === 11 ? selectedYear + 1 : selectedYear;
    const nextKey = getKeyForDate(new Date(nextYear, nextMonth));

    if (currentKey !== key && !loadedMonthsRef.current.has(key)) {
      fetchMonthSlots(
        selectedMonth,
        selectedYear,
        values,
        form,
        loadedMonthsRef.current,
        timeSlotsMapRef.current,
      );
    }

    if (!loadedMonthsRef.current.has(nextKey)) {
      fetchMonthSlots(
        nextMonth,
        nextYear,
        values,
        form,
        loadedMonthsRef.current,
        timeSlotsMapRef.current,
      );
    }

    setCurrentKey(key);
  };

  const getTimeOptions = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date)) {
      return [];
    }

    return timeSlotsMapRef.current.get(DateTime.fromJSDate(date).toFormat('yyyy-MM-dd')) || [];
  };

  useQuery(
    [
      'serviceAreaCheck',
      values.portalId,
      values.geoData?.latitude,
      values.geoData?.longitude,
    ],
    isInServiceArea,
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: (
        !!values.geoData?.latitude
      && !!values.geoData?.longitude
      ),
      onSuccess: ({ data: { isInServiceArea: flag } }) => {
        setFieldValue('isAddressInArea', flag);
      },
    },
  );

  return (
    <S.Content>
      <S.Header>
        Book Online
      </S.Header>

      <S.FieldContainer>
        <S.Label>Address</S.Label>

        <FormikField
          name="address"
          component={LocationInput}
          label="Property"
          isGap={false}
        />
      </S.FieldContainer>

      <S.FieldContainer>
        <S.Label>Unit/Suite/Apt. number (optional)</S.Label>

        <FormikField
          name="unitNumber"
          component={Input}
          label="Unit Number"
          isGap={false}
        />
      </S.FieldContainer>

      {showDateTimePicker && (
        <>
          <S.FieldContainer>
            <S.Label>If available, what day works best for you?</S.Label>

            <FormikField
              name="date"
              component={FormikDatepicker}
              selectRange={false}
              shouldDisableDay={(date) => !filterDate(date, form, timeSlotsMapRef.current)}
              isGap={false}
              disableYearNavigation
              disableViewChange
              showOutsideDays={false}
              selected={values.date}
              minDate={minDateTime.toJSDate()}
              onChange={(newDate) => handleDateChange(newDate, setFieldValue, values, updateEventMap)}
              onMonthChange={handleMonthChange}
              allowDeselect={!requireDateTime}
            />
          </S.FieldContainer>

          <S.FieldContainer>
            <S.Label>What are your preferred arrival times?</S.Label>

            <FormikField
              name="time"
              placeholder="Any time"
              component={FormikSelect}
              label="Arrival time"
              options={getTimeOptions(values.date)}
              isDisabled={values.customTimeRangeEnabled && !(values.date instanceof Date)}
              closeOnSelect
              isGap={false}
              disabled={!values.date}
              allowDeselect
            />
          </S.FieldContainer>
        </>
      )}

      {!user.email && (
        <S.FieldContainer>
          <S.Label>Email</S.Label>

          <FormikField
            name="email"
            component={Input}
            label="Email"
            type="email"
            isGap={false}
          />
        </S.FieldContainer>
      )}
    </S.Content>
  );
};

export const workingScheduleItemPropTypes = PropTypes.shape({
  start: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.object.isRequired,
  ]),
  end: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.object.isRequired,
  ]),
  isWorkingDay: PropTypes.bool.isRequired,
  weekDay: PropTypes.number.isRequired,
});

ContactDetailsStep.propTypes = {
  form: PropTypes.shape({
    customTimeRange: PropTypes.number,
    workingSchedule: PropTypes.arrayOf(workingScheduleItemPropTypes).isRequired,
    services: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    fields: PropTypes.arrayOf(PropTypes.shape({})),
    showDateTimePicker: PropTypes.bool,
    requireDateTime: PropTypes.bool,
  }).isRequired,
  values: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string,
    services: PropTypes.arrayOf(PropTypes.string),
    address: PropTypes.string,
    geoData: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    unitNumber: PropTypes.string,
    customTimeRangeEnabled: PropTypes.bool,
    portalId: PropTypes.string,
    isAddressInArea: PropTypes.bool,
    email: PropTypes.string,
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string,
    services: PropTypes.string,
    address: PropTypes.string,
    unitNumber: PropTypes.string,
    isInServiceArea: PropTypes.string,
  }),
};

ContactDetailsStep.defaultProps = {
  errors: {},
};

export default ContactDetailsStep;
